"use client";
import { usePostHog } from "posthog-js/react";
import { useCallback, useRef } from "react";
import { AnalyticsEvent } from "@/constants";
import { getAsPath } from "@/store";

interface AnalyticsProperties {
  [key: string]: any;
}

interface ReferrerInfo {
  referrer?: string;
  url?: string;
  from?: string;
}

/**
 * List of PII (Personally Identifiable Information) fields that should never be sent to analytics
 */
const PII_FIELDS = [
  "email",
  "phone",
  "phoneNumber",
  "phone_number",
  "address",
  "ssn",
  "social_security",
  "password",
  "token",
  "accessToken",
  "refreshToken",
  "apiKey",
  "creditCard",
  "credit_card",
  "firstname",
  "lastname",
  "firstName",
  "lastName",
  "fullName",
  "full_name",
] as const;

/**
 * Sanitize properties by removing PII fields before sending to PostHog
 * @param properties - The properties object to sanitize
 * @returns A new object with PII fields removed
 */
const sanitizeProperties = (
  properties: Record<string, any>,
): Record<string, any> => {
  if (!properties || typeof properties !== "object") {
    return {};
  }

  const sanitized = { ...properties };

  // Remove PII fields
  PII_FIELDS.forEach((field) => {
    delete sanitized[field];
  });

  // Also check for nested objects (one level deep)
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] && typeof sanitized[key] === "object") {
      sanitized[key] = sanitizeProperties(sanitized[key]);
    }
  });

  return sanitized;
};

/**
 * Check if a user is an internal user based on their email domain
 */
const isInternalUser = (email?: string): boolean => {
  if (!email) return false;
  const internalDomains = ["@meetwithfocal.com"];
  return internalDomains.some((domain) =>
    email.toLowerCase().includes(domain.toLowerCase()),
  );
};

export const useAnalytics = () => {
  const posthog = usePostHog();
  const path = getAsPath();
  // Track if current user is internal to avoid repeated checks
  const isInternalUserRef = useRef<boolean>(false);

  const getReferrerInfo = useCallback((): ReferrerInfo => {
    return {
      referrer:
        typeof window !== "undefined"
          ? document.referrer || undefined
          : undefined,
      url: path.currentAsPath,
      from: path.prevAsPath,
    };
  }, [path]);

  // Main event tracking method
  const track = useCallback(
    (
      event: AnalyticsEvent | string,
      properties?: AnalyticsProperties,
      options?: {
        includeReferrer?: boolean;
        timestamp?: boolean;
      },
    ) => {
      if (!posthog) {
        console.warn("PostHog not initialized");
        return;
      }

      // Skip tracking for internal users
      if (isInternalUserRef.current) {
        console.log(
          "[Analytics] Skipping event tracking for internal user:",
          event,
        );
        return;
      }

      try {
        let eventProperties: AnalyticsProperties = { ...properties };

        // Add referrer info if requested
        if (options?.includeReferrer) {
          Object.assign(eventProperties, getReferrerInfo());
        }

        // Add timestamp if requested
        if (options?.timestamp) {
          eventProperties.timestamp = new Date().toISOString();
        }

        // Sanitize properties to remove any PII before sending
        const sanitizedProperties = sanitizeProperties(eventProperties);

        posthog.capture(event, sanitizedProperties);
      } catch (error) {
        console.error("Analytics tracking error:", error);
      }
    },
    [posthog, getReferrerInfo],
  );

  // User identification
  const identify = useCallback(
    (
      userId: string,
      userProperties?: AnalyticsProperties & { email?: string },
    ) => {
      if (!posthog) return;

      // Check if this is an internal user and skip tracking
      if (userProperties?.email && isInternalUser(userProperties.email)) {
        isInternalUserRef.current = true;
        console.log(
          "[Analytics] Skipping tracking for internal user:",
          userProperties.email,
        );
        return;
      }

      isInternalUserRef.current = false;

      try {
        // Sanitize properties to remove all PII before sending to PostHog
        const sanitizedProperties = sanitizeProperties(userProperties || {});

        posthog.identify(userId, sanitizedProperties);
      } catch (error) {
        console.error("Analytics identify error:", error);
      }
    },
    [posthog],
  );

  // Set user properties
  const setUserProperties = useCallback(
    (properties: AnalyticsProperties) => {
      if (!posthog) return;

      // Skip setting properties for internal users
      if (isInternalUserRef.current) {
        console.log("[Analytics] Skipping setUserProperties for internal user");
        return;
      }

      try {
        // Sanitize properties to remove any PII before sending
        const sanitizedProperties = sanitizeProperties(properties);

        posthog.people.set(sanitizedProperties);
      } catch (error) {
        console.error("Analytics setUserProperties error:", error);
      }
    },
    [posthog],
  );

  // Reset user (logout)
  const reset = useCallback(() => {
    if (!posthog) return;

    try {
      posthog.reset();
    } catch (error) {
      console.error("Analytics reset error:", error);
    }
  }, [posthog]);

  return {
    track,
    identify,
    setUserProperties,
    reset,
    posthog,
  };
};
